import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { DjayToetsAvatar } from '../../mascot';
import type { AssessmentReport } from '../types';
interface AssignedAssessmentCompleteScreenProps {
  report: AssessmentReport;
  onExit: () => void;
}
export function AssignedAssessmentCompleteScreen({
  report,
  onExit
}: AssignedAssessmentCompleteScreenProps) {
  const totalQuestions = report.score.totalQuestions;
  const correctCount = report.score.correctCount;
  const score = report.score.percentage;
  const answers = report.answers ?? [];
  return <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white flex items-center justify-center p-4" data-id="element-1006" data-mp="30lnd3bhoshcv">
      <motion.div initial={{
      opacity: 0,
      scale: 0.9,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} transition={{
      duration: 0.5,
      ease: 'easeOut'
    }} className="w-full max-w-md" data-id="element-1007" data-mp="35lrtl1ilhc33">
        <Card className="p-8 text-center shadow-lg border-orange-100" data-id="element-1008" data-mp="2b2b4v9373cz2">
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          delay: 0.2,
          type: 'spring',
          stiffness: 200,
          damping: 15
        }} className="mb-6" data-id="element-1009" data-mp="261wuhke4fqxv">
            <DjayToetsAvatar domain="toets" size="lg" animate data-id="element-1010" data-mp="2tt2ca53mgtat" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} data-id="element-1011" data-mp="2oxwof0qnt4k3">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-green-100 rounded-full mb-4" data-id="element-1012" data-mp="3cosftwr7dxqt">
              <CheckCircle2 className="w-8 h-8 text-green-600" data-id="element-1013" data-mp="1asjrmcm558vj" />
            </div>

            <h1 className="text-2xl font-black text-slate-900 mb-2" data-id="element-1014" data-mp="1fpth1s9j5kjd">
              Toets klaar! 🎉
            </h1>
            <p className="text-slate-600 mb-6" data-id="element-1015" data-mp="111yme6byp63n">
              Goed gedaan! Je hebt de toets afgerond.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6
        }} className="bg-slate-50 rounded-xl p-4 mb-4" data-id="element-1016" data-mp="s1sdkxr7kuat">
            <div className="grid grid-cols-2 gap-4" data-id="element-1017" data-mp="n3iguipu5ln3">
              <div data-id="element-1018" data-mp="n4acftjfnjdd">
                <div className="text-2xl font-black text-orange-600" data-id="element-1019" data-mp="2chnbp4couh7m">
                  {Math.round(score)}%
                </div>
                <div className="text-xs font-medium text-slate-500 mt-0.5" data-id="element-1020" data-mp="2hff9gf00kxjq">
                  Score
                </div>
              </div>
              <div data-id="element-1021" data-mp="5qsdnlh707ud">
                <div className="text-2xl font-black text-slate-800" data-id="element-1022" data-mp="uaaur5fajko">
                  {correctCount}/{totalQuestions}
                </div>
                <div className="text-xs font-medium text-slate-500 mt-0.5" data-id="element-1023" data-mp="fjpza0hhecx7">
                  Goed beantwoord
                </div>
              </div>
            </div>
          </motion.div>

          {/* Per-question goed/fout summary */}
          {answers.length > 0 && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.7
        }} className="mb-6" data-id="element-1024" data-mp="jlb91cypyb36">
              <h2 className="text-sm font-bold text-slate-700 mb-3 text-left" data-id="element-1025" data-mp="309p4w3b209wn">
                Overzicht per vraag
              </h2>
              <div className="grid grid-cols-5 sm:grid-cols-6 gap-2" data-id="element-1026" data-mp="2vcf822lc4vys">
                {answers.map((answer, index) => <motion.div key={answer.questionId} initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 0.75 + index * 0.03
            }} className={`
                      flex flex-col items-center justify-center rounded-lg py-2 px-1
                      ${answer.isCorrect ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}
                    `} data-id="element-1027" data-mp="1rbpbofxm2joq" data-mpi={index}>
                    <span className="text-xs font-bold mb-0.5" data-id="element-1028" data-mp="kwelubkf7a76">
                      {index + 1}
                    </span>
                    {answer.isCorrect ? <CheckCircle2 className="w-4 h-4" data-id="element-1029" data-mp="ovnete7mz41o" /> : <XCircle className="w-4 h-4" data-id="element-1030" data-mp="hdpb5qc4yn6d" />}
                  </motion.div>)}
              </div>
            </motion.div>}

          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.9
        }} data-id="element-1031" data-mp="8choy6mw9csn">
            <p className="text-sm text-slate-500 mb-5" data-id="element-1032" data-mp="3um24v118srrp">
              Je docent kan de resultaten bekijken in het leerkrachtportaal.
            </p>

            <Button onClick={onExit} className="w-full bg-orange-500 hover:bg-orange-600 text-white shadow-sm" size="lg" data-id="element-1033" data-mp="3kufwdcgah6sn">
              Terug naar home
              <ArrowRight className="w-4 h-4 ml-2" data-id="element-1034" data-mp="3buj30xiyfjr9" />
            </Button>
          </motion.div>
        </Card>
      </motion.div>
    </div>;
}